import { NavLink, useNavigate, useSearchParams } from "react-router"
import PageTitle from "../_general/PageTitle"
import LoaderText from "../_general/LoaderText"
import { css } from "@emotion/react"
import { useIntl } from "react-intl"
import { useCallback, useContext } from "react"
import PitchContext from "../../context/Pitch"
import PitchIndexPdfContext, { PitchIndexPdfDataProvider } from "../../context/PitchIndexPdfData"
import { colors, fonts, isIndexResultValid, getSortedPerformanceIndexResults } from "shared-ui"
import AnalysisDate from "../analysis/AnalysisDate"
import { useMemo } from "react"
import { getUnixTime } from "date-fns"
import IndexResultCard from "../index/IndexResultCard"
import IndexOverviewCard from "../index/IndexOverviewCard"
import FancyDropdown from "../_control/FancyDropdown"
import PitchListContext from "../../context/PitchList"
import ProfileContext from "../../context/Profile"
import useRawState from "../../hooks/useRawState"
import Loader from "../_general/Loader"
import LocalStorage from "../../services/LocalStorage"
import { useEffect, useState } from "react"
import VisibilityToggle from "../_control/VisibilityToggle"
import pdfDownload from '@assets/icons/pdf-download.svg'
import { ReactSVG } from "react-svg"

export default function PitchIndexPage({ ...props }) {

    const { formatMessage } = useIntl()
    const { data: profile } = useContext(ProfileContext)
    const allowPerformanceFeature = profile?.featureFlags?.includes('performance')

    const [searchParams] = useSearchParams()
    const navigate = useNavigate()

    const { data: pitch } = useContext(PitchContext)
    const { pitches = [] } = useContext(PitchListContext)

    const timestamp = useMemo(() => {
        const nowUnix = getUnixTime(new Date())
        const timestampParam = searchParams.get('timestamp') ?? nowUnix
        const num = Number(timestampParam)
        if (num === 0 || !Number.isFinite(num)) return nowUnix
        return num
    }, [searchParams])

    const { data: latestIndices, isBusy: loadingLatestIndices } = useRawState(
        !!pitch ?
            `/api/current/pitches/${pitch.id}/index-results/latest/detail`
            : undefined,
        {
            to: timestamp,
        }
    )

    const latestIndicesEnriched = useMemo(() => {
        return latestIndices?.map(ind => ({
            ...ind,
            _isExpired_: !isIndexResultValid(ind, new Date(timestamp * 1000))
        }))
    }, [latestIndices])

    const pitchFilterOptions = useMemo(() => {
        const options = pitches.map(p => ({
            value: p.id,
            label: p.name
        }))
        options.unshift({
            value: null,
            label: formatMessage({ id: 'allSportsFields' })
        })
        return options
    }, [pitches])

    const handlePitchChange = useCallback((e) => {
        const pitchId = e.value
        navigate(`/pitch/${pitchId}/index?timestamp=${timestamp}`)
    }, [timestamp])

    const [showExpired, setShowExpired] = useState(LocalStorage.getItem('showExpiredIndices') ?? true)
    useEffect(() => {
        LocalStorage.setItem('showExpiredIndices', showExpired)
    }, [showExpired])

    if (!pitch) return null
    if (!allowPerformanceFeature) return null

    return <PitchIndexPdfDataProvider>
        <div css={css`max-width: 2200px;`}  {...props}  >

            <Header
                pitch={pitch}
                timestamp={timestamp}
                indexResults={latestIndicesEnriched}
                pitchFilterOptions={pitchFilterOptions}
                handlePitchChange={handlePitchChange}
                showExpired={showExpired}
                setShowExpired={setShowExpired}
            />

            {loadingLatestIndices ?
                <Loader size={'2em'} />
                : latestIndicesEnriched ?
                    <div css={css`display: grid; gap: 0.5em;`}>

                        <IndexOverviewCard indexData={latestIndicesEnriched} referenceTimestamp={timestamp} showExpired={showExpired} setShowExpired={setShowExpired} />
                        <div css={css`height: 0em;`} />

                        {getSortedPerformanceIndexResults(latestIndicesEnriched)
                            .map((ind, i) => <div
                                key={`ind-${i}`}
                                css={ind._isExpired_ ? style.isExpired(showExpired) : null}
                            >
                                <IndexResultCard indexData={ind} />
                            </div>)
                        }
                    </div>
                    : null
            }
        </div>
    </PitchIndexPdfDataProvider>
}

function Header({ timestamp, pitch, indexResults, pitchFilterOptions, handlePitchChange, showExpired, setShowExpired, ...props }) {

    const { formatMessage } = useIntl()

    const {
        fetchPDF, dataReady, setIndexResults, setReferenceTimestamp,
        setShowExpired: setPdfShowExpired,
    } = useContext(PitchIndexPdfContext)
    const [pdfBusy, setPdfBusy] = useState(false)

    useEffect(() => {
        // propagate falsy values too: a refetch (e.g. changing the timestamp param)
        // transiently clears indexResults, and the PDF context must clear with it so
        // dataReady doesn't stay true with a stale snapshot from the previous fetch
        setIndexResults(indexResults)
    }, [indexResults])

    useEffect(() => {
        if (!timestamp) return
        setReferenceTimestamp(timestamp)
    }, [timestamp])

    // keep the PDF context in sync with the page's own expired-data visibility toggle
    useEffect(() => {
        setPdfShowExpired(showExpired)
    }, [showExpired])

    const requestPDF = useCallback(async () => {
        setPdfBusy(true)
        try {
            await fetchPDF()
        } catch (e) {
            console.log(e)
        }
        setPdfBusy(false)
    }, [fetchPDF])

    const hasClub = !!pitch.club?.id

    const clubLinkCss = css`
        text-decoration: none; 
        color: inherit;
        cursor: ${hasClub ? 'pointer' : 'unset'};
    `
    const clubLinkMsg = pitch.club?.name || formatMessage({ id: 'noClub' })

    const [searchParams] = useSearchParams()
    const hasUrlTimestamp = !!searchParams.get('timestamp')

    return <div css={style.header} >
        <PageTitle css={style.title}>

            {/* club */}
            {pitch ?
                <NavLink
                    css={clubLinkCss}
                    to={hasClub ? `/club/${pitch.club?.id}` : undefined}
                >
                    {clubLinkMsg}
                </NavLink>
                :
                <LoaderText subtle />
            }
            &nbsp;{'/'}&nbsp;

            {/* pitch */}
            {pitch ?
                <NavLink
                    css={css`text-decoration: none; color: inherit;`}
                    to={`/analyses?pitch=${pitch.id}`}>
                    {pitch.name}
                </NavLink>
                :
                <LoaderText subtle />
            }
            &nbsp;{'/'}&nbsp;

            {/* index */}
            <span css={css`color: ${colors.main1};`}>
                {formatMessage({ id: 'index' })}
            </span>
            <span css={css`color: ${colors.main1};`}>
                &nbsp;{'/'}&nbsp;
                {hasUrlTimestamp ?
                    <AnalysisDate
                        date={timestamp}
                        timeZone={pitch?.venue?.timeZone}
                        css={style.analysisDate}
                    />
                    :
                    formatMessage({ id: 'today' })
                }
            </span>

        </PageTitle>

        <div css={css`display: flex; gap: 2em; align-items: center;`}>

            <ReactSVG
                src={pdfDownload}
                title={formatMessage({ id: 'downloadPDF' })}
                onClick={() => {
                    if (dataReady && !pdfBusy) {
                        requestPDF()
                    }
                }}
                css={[style.pdfIcon, dataReady && !pdfBusy ?
                    style.pdfEnabled : pdfBusy ? style.pdfBusy : style.pdfForbidden
                ]}
            />

            <VisibilityToggle
                isVisible={showExpired}
                setIsVisible={setShowExpired}
                title={formatMessage({ id: 'toggleVisibilityOfOldData' })}
            />
            <FancyDropdown
                value={pitchFilterOptions.find(o => o.value === pitch.id)}
                onChange={handlePitchChange}
                options={pitchFilterOptions}
                css={css`min-width: 16em;`}
            />
        </div>
    </div>
}

const style = {
    header: css`
        display: flex; 
        justify-content: space-between; 
        align-items: center; 
        margin-bottom: 2em;
        flex-wrap: wrap;
        gap: 2em;
    `,
    title: css`
        margin-bottom: 0; 
        display: flex; 
        flex-wrap: wrap;
    `,
    analysisDate: css`
        font-family: ${fonts.main};
        font-weight: unset;
        font-size: 1em;
    `,
    pdfIcon: css`
        width: 2.5em;
        height: 2.5em;
        fill: ${colors.soft};
        border-radius: 5px;
        padding: 0.25em;
    `,
    pdfEnabled: css`
        cursor: pointer;
        &:hover {
            fill: ${colors.white};
            background: ${colors.eventLight};
        }
    `,
    pdfBusy: css`
        cursor: progress;
        opacity: 0.3;
    `,
    pdfForbidden: css`
        cursor: not-allowed;
        opacity: 0.3;
    `,
    isExpired: (showExpired) => css`
        ${!showExpired && 'display: none;'}
        > * > * {
            filter: grayscale(1);
        }

        &:hover {
            > * > * {
                filter: unset;
            }
        }
    `
}